import "./quoting-accordion.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { ExpansionPanelActionEvent } from "@tql/ui-components-react/kendo/layout";
import {
	ExpansionPanel,
	ExpansionPanelContent,
} from "@tql/ui-components-react/kendo/layout";
import { Reveal } from "@tql/ui-components-react/tql/animation";
import {
	chevronRight,
	chevronUp,
} from "@tql/ui-components-react/untitled/icons";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

export type Step = {
	step: number;
	title: string;
	id: string;
	active: boolean;
	completed: boolean;
	expandCompleted?: boolean;
	completedHeader?: ReactNode;
	content: ReactNode;
	completedContent?: ReactNode;
};

type TitleProps = {
	step: Step;
	isExpanded: boolean;
};

const Title = ({ step, isExpanded }: TitleProps) => {
	return (
		<div className={"step-title-container"}>
			{isExpanded ? (
				<SvgIcon icon={chevronUp} size={"xlarge"} />
			) : (
				<SvgIcon icon={chevronRight} size={"xlarge"} />
			)}
			<div>
				<div className={"step-step"}>{step.step}</div>
			</div>

			<div className={"step-title"}>{step.title}</div>
		</div>
	);
};

type QuotingAccordionProps = {
	steps: Step[];
};

// Kendo's ExpansionPanel preventDefaults any Enter/Space reaching it, which cancels activation of controls inside the panel.
const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
	if (e.key === " " || e.key === "Enter") {
		e.stopPropagation();
	}
};

export const FtlQuotingAccordion = ({ steps }: QuotingAccordionProps) => {
	const [expandedPanels, setExpandedPanels] = useState<Set<string>>(
		new Set(steps.filter((step) => step.active).map((step) => step.id)),
	);

	// Track which steps have been manually toggled by the user
	// eslint-disable-next-line react/hook-use-state -- Only need setter to trigger re-renders, state value tracked in ref
	const [, setManuallyToggled] = useState<Set<string>>(new Set());
	const manuallyToggledRef = useRef<Set<string>>(new Set());

	// Track previous active states
	const prevActiveSteps = useRef<Set<string>>(
		new Set(steps.filter((s) => s.active).map((s) => s.id)),
	);

	function setsAreEqual<T>(a: Set<T>, b: Set<T>): boolean {
		if (a.size !== b.size) {
			return false;
		}
		for (const value of a) {
			if (!b.has(value)) {
				return false;
			}
		}
		return true;
	}

	// Collapse completed steps when they become inactive, but allow manual re-opening
	useEffect(() => {
		const currentActive = new Set(
			steps.filter((s) => s.active).map((s) => s.id),
		);

		// Always clear manual toggles for steps that just completed
		// This ensures they collapse every time they complete
		const newManuallyToggled = new Set(manuallyToggledRef.current);
		for (const step of steps) {
			// If step was active before and is now completed+inactive, always remove from manual toggles
			if (
				step.completed &&
				!step.active &&
				prevActiveSteps.current.has(step.id)
			) {
				newManuallyToggled.delete(step.id);
			}
		}

		if (!setsAreEqual(manuallyToggledRef.current, newManuallyToggled)) {
			manuallyToggledRef.current = newManuallyToggled;
			setManuallyToggled(newManuallyToggled);
		}

		prevActiveSteps.current = currentActive;

		setExpandedPanels((prev) => {
			const newSet = new Set(prev);

			// Remove completed steps that are no longer active (auto-collapse on completion)
			// BUT only if they haven't been manually toggled by the user
			for (const step of steps) {
				if (
					step.completed &&
					!step.active &&
					newSet.has(step.id) &&
					!manuallyToggledRef.current.has(step.id)
				) {
					newSet.delete(step.id);
				}
				// Ensure active steps are always expanded
				if (step.active && !newSet.has(step.id)) {
					newSet.add(step.id);
				}
			}

			return setsAreEqual(prev, newSet) ? prev : newSet;
		});
	}, [steps]);

	const handlePanelAction = (
		event: ExpansionPanelActionEvent,
		stepId: string,
		step: Step,
	) => {
		if (step.completed) {
			// Mark this step as manually toggled in both state and ref
			const newManuallyToggled = new Set(manuallyToggledRef.current).add(
				stepId,
			);
			manuallyToggledRef.current = newManuallyToggled;
			setManuallyToggled(newManuallyToggled);

			setExpandedPanels((prev) => {
				const newSet = new Set(prev);
				if (event.expanded) {
					newSet.delete(stepId);
				} else {
					newSet.add(stepId);
				}
				return newSet;
			});
		}
	};

	const isPanelExpanded = (step: Step): boolean => {
		if (step.active) {
			return true;
		}
		if (step.completed && (step.expandCompleted ?? true)) {
			return expandedPanels.has(step.id);
		}
		return false;
	};

	return (
		<div className={"quoting-accordion-wrapper"} role={"region"}>
			{steps.map((item) => (
				<ExpansionPanel
					key={item.id}
					className={item.completed ? "completed" : ""}
					disabled={!(item.active || item.completed)}
					expanded={isPanelExpanded(item)}
					onAction={(event: ExpansionPanelActionEvent) => {
						handlePanelAction(event, item.id, item);
					}}
					subtitle={item.completed ? item.completedHeader : null}
					// tabIndex={0}
					title={<Title isExpanded={isPanelExpanded(item)} step={item} />}
				>
					<Reveal>
						{isPanelExpanded(item) && (
							<ExpansionPanelContent>
								<div onKeyDown={onKeyDown} role={"presentation"}>
									{item.completed && item.completedContent
										? item.completedContent
										: item.content}
								</div>
							</ExpansionPanelContent>
						)}
					</Reveal>
				</ExpansionPanel>
			))}
		</div>
	);
};
